import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DebounceField } from '@openx/components-core/lib/DebounceField/DebounceField.tsx';
import { useDesignSystemEnabled } from '@openx/components-core/lib/theme/useDesignSystemEnabled.ts';
import { FiltersBar } from '@openx/components-filters/lib/FiltersBar/FiltersBar.tsx';
import type { ChipFilterValue, FiltersConfig } from '@openx/types/chipFilters.ts';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChipFiltersView } from './ChipFiltersView.tsx';

const StyledContainerGrid = styled(Grid)`
  background: ${({ theme }) => theme.palette.background.boxDarker};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  min-height: 48px;
  padding: ${({ theme }) => theme.spacing(1, 4, 0, 4)};
  position: relative;
`;

const StyledCloseIconButton = styled(IconButton)`
  position: absolute;
  right: 8px;
`;

// The legacy theme reserves room for the absolutely positioned ✕ (`inputTypeSearch` right padding) and hides the
// native search cancel button; the design-system OutlinedInput does neither, so the DS field puts the ✕ in an
// end adornment (which takes its own space) and hides the native button itself.
const designSystemSearchSx = { '& .MuiInputBase-input::-webkit-search-cancel-button': { display: 'none' } };

const StyledTypography = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.disabled};
  padding: ${({ theme }) => theme.spacing(1.25, 1.5)};
`;

const StyledSnackbar = styled(Snackbar)`
  & {
    position: absolute;
    bottom: auto;
    left: auto;
    top: 41px;
  }
`;

interface TopBarProps<FilterType extends { phrase: string }> {
  filters: FilterType;
  onChange: (filters: FilterType) => void;
  searchText?: string;
  chipFiltersConfig?: FiltersConfig;
  minLength?: number;
  customPopoverContent?: ReactNode;
  onPhraseChange?: (text: string) => void;
  autofocus?: boolean;
  fullWidth?: boolean;
  // workaround for new filters
  useFiltersV2?: boolean;
}

export function TopBar<FilterType extends { phrase: string }>({
  filters,
  onChange,
  onPhraseChange,
  searchText,
  chipFiltersConfig,
  minLength = 2,
  customPopoverContent,
  autofocus = true,
  fullWidth = false,
  useFiltersV2 = false,
}: TopBarProps<FilterType>) {
  const { t } = useTranslation();
  const isDesignSystem = useDesignSystemEnabled('textField');

  const [searchBarAnchor, setSearchBarAnchor] = useState<null | HTMLElement>(null);
  const [textLength, setTextLength] = useState(0);

  const isPopperOpen = Boolean(searchBarAnchor);

  const clearSearchValue = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSearchBarAnchor(null);

    const newFilters = { ...filters, phrase: '' };

    onPhraseChange ? onPhraseChange('') : onChange(newFilters);
    setTextLength(0);
  };

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const input: null | HTMLInputElement = event.currentTarget.getElementsByTagName('input').item(0);

    setSearchBarAnchor(event.currentTarget);
    setTextLength(input?.value.length ?? 0);
    event.stopPropagation();
  }, []);

  const hidePopper = useCallback(() => setSearchBarAnchor(null), []);

  const onFilterChange = (newFilters: { [categoryName: string]: ChipFilterValue }) => {
    onChange({ ...filters, ...newFilters });
  };

  const phraseChangeHandler = useCallback(
    event => {
      const val = event.target.value;

      const searchPhraseLength = val.length;
      setTextLength(searchPhraseLength);

      if (searchPhraseLength > minLength || !searchPhraseLength) {
        onPhraseChange ? onPhraseChange(val) : onChange({ ...filters, phrase: val });
      }
    },
    [filters, minLength, onChange, onPhraseChange]
  );

  const preventEventPropagation = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  useEffect(() => {
    document.addEventListener('click', hidePopper);

    return () => {
      document.removeEventListener('click', hidePopper);
    };
  }, [hidePopper]);

  return (
    <StyledContainerGrid container>
      {searchText && (
        <Grid
          container
          size={fullWidth ? 12 : 4}
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Grid
            container
            sx={{
              flexGrow: 1,
            }}
          >
            <DebounceField
              key={filters.phrase ? 'with-value' : 'without-value'}
              placeholder={searchText}
              type="search"
              autoFocus={autofocus}
              onChange={phraseChangeHandler}
              onClick={handleClick}
              value={filters.phrase}
              fullWidth
              debounceTimeout={500}
              data-test="search-field"
              sx={isDesignSystem ? designSystemSearchSx : undefined}
              slotProps={{
                input: isDesignSystem
                  ? {
                      endAdornment: filters.phrase && (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t('Clear search')}
                            data-test="close-search"
                            size="small"
                            onClick={clearSearchValue}
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : {
                      disableUnderline: true,
                      endAdornment: filters.phrase && (
                        <StyledCloseIconButton data-test="close-search" size="small" onClick={clearSearchValue}>
                          <CloseIcon />
                        </StyledCloseIconButton>
                      ),
                    },
              }}
            />

            <StyledSnackbar onClick={preventEventPropagation} open={isPopperOpen}>
              <Paper sx={{ width: `${searchBarAnchor?.offsetWidth}px` }}>
                {textLength <= minLength && (
                  <StyledTypography data-test="search-placeholder" variant="body1">
                    {t('Provide at least {count} characters', { count: minLength + 1 })}
                  </StyledTypography>
                )}
                {customPopoverContent}
              </Paper>
            </StyledSnackbar>
          </Grid>
        </Grid>
      )}
      {chipFiltersConfig && (
        <Grid size={8}>
          <ChipFiltersView filtersConfig={chipFiltersConfig} filters={filters} onFilterChange={onFilterChange} />
        </Grid>
      )}

      {useFiltersV2 && (
        <Grid size={8} onClick={preventEventPropagation}>
          <FiltersBar />
        </Grid>
      )}
    </StyledContainerGrid>
  );
}
